import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useHomeFlights } from '../hooks/useHomeFlights.js';
import { useSiteLang } from '../hooks/useSiteLang.js';
import {
  airlineLogoSrc,
  DEFAULT_AIRLINE_LOGO,
  isIataAirlineCode,
  localizedStatus,
  placeLabel,
  searchHaystack,
  statusClass,
} from '../utils/flights.js';

function AirlineCell({ flight }) {
  return (
    <span className="v4-airline-cell">
      <img
        className="v4-airline-logo"
        src={airlineLogoSrc(flight)}
        alt={flight.airline || 'Airline'}
        width="44"
        height="44"
        loading="lazy"
        decoding="async"
        referrerPolicy="no-referrer"
        onError={(e) => {
          e.currentTarget.src = DEFAULT_AIRLINE_LOGO;
        }}
      />
      {isIataAirlineCode(flight.airlineCode) && (
        <span className="v4-airline-code" aria-hidden="true">
          {String(flight.airlineCode).toUpperCase().replace(/[^A-Z0-9]/g, '')}
        </span>
      )}
    </span>
  );
}

function PlaceCell({ flight, dir, lang }) {
  const { city, code } = placeLabel(flight, dir, lang);
  return (
    <span className="notranslate" translate="no">
      {city}
      {code && code !== '—' && (
        <>
          {' '}
          <span className="v4-iata">({code})</span>
        </>
      )}
    </span>
  );
}

export default function FlightBoard() {
  const lang = useSiteLang();
  const [board, setBoard] = useState('arr');
  const [filter, setFilter] = useState('');
  const { arrivals: liveArrivals, departures: liveDepartures, updatedLabel, loading, error } =
    useHomeFlights({ limit: 6 });

  const arrivals = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return liveArrivals;
    return liveArrivals.filter((f) => searchHaystack(f, 'arr').includes(q));
  }, [filter, liveArrivals]);

  const departures = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return liveDepartures;
    return liveDepartures.filter((f) => searchHaystack(f, 'dep').includes(q));
  }, [filter, liveDepartures]);

  const emptyMessage = (kind) => {
    if (loading) return 'Loading flights…';
    if (error) return 'Unable to load live flights right now.';
    return kind === 'arr' ? 'No arrivals available.' : 'No departures available.';
  };

  return (
    <section className="bg-nkc-mist py-16 sm:py-20" id="flights">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="v4-reveal mb-8 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-nkc-gold">Live flight board</p>
            <h2 className="mt-2 font-display text-3xl font-bold text-nkc-navy sm:text-4xl">
              Arrivals &amp; departures
            </h2>
          </div>
          <Link to="/flights" className="text-sm font-bold text-nkc-blue hover:underline">
            Open full board →
          </Link>
        </div>

        <div className="v4-fids v4-fids--light v4-reveal overflow-hidden rounded-3xl p-4 sm:p-6">
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="inline-flex rounded-full bg-nkc-mist p-1 ring-1 ring-nkc-blue/10" role="tablist">
              {[
                { id: 'arr', label: 'Arrivals' },
                { id: 'dep', label: 'Departures' },
              ].map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  className={`v4-board-tab rounded-full border border-transparent px-4 py-2 text-sm font-semibold${board === tab.id ? ' is-active text-white' : ' text-nkc-muted'}`}
                  role="tab"
                  aria-selected={board === tab.id}
                  onClick={() => setBoard(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            <label className="relative block w-full sm:max-w-xs">
              <span className="sr-only">Filter flights</span>
              <input
                type="search"
                placeholder="Filter by flight, city, airline…"
                className="w-full rounded-2xl border border-nkc-blue/15 bg-white px-4 py-2.5 text-sm text-nkc-ink placeholder:text-nkc-muted outline-none ring-nkc-sky focus:ring-2"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
              />
            </label>
          </div>

          {board === 'arr' ? (
            <div className="overflow-x-auto rounded-2xl bg-white ring-1 ring-nkc-blue/10">
              <table className="v4-board-table v4-board-table--light min-w-full">
                <thead>
                  <tr>
                    <th>Airline</th>
                    <th>Flight</th>
                    <th>Origin</th>
                    <th>Scheduled</th>
                    <th>Actual</th>
                    <th>Status</th>
                    <th>Baggage</th>
                  </tr>
                </thead>
                <tbody>
                  {arrivals.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="!text-center text-nkc-muted">
                        {emptyMessage('arr')}
                      </td>
                    </tr>
                  ) : (
                    arrivals.map((f) => (
                      <tr key={`${f.flight}-${f.sta}-${f.originCode}`} className="v4-board-row">
                        <td>
                          <AirlineCell flight={f} />
                        </td>
                        <td>
                          <strong className="text-nkc-navy">{f.flight}</strong>
                        </td>
                        <td>
                          <PlaceCell flight={f} dir="arr" lang={lang} />
                        </td>
                        <td className="font-mono tabular-nums text-nkc-muted">{f.sta || '—'}</td>
                        <td className="font-mono font-semibold tabular-nums text-nkc-blue">
                          {(f.eta || '—') !== '—' ? f.eta : f.sta || '—'}
                        </td>
                        <td>
                          <span className={`${statusClass(f.statusKey)} notranslate`} translate="no">
                            {localizedStatus(f, lang)}
                          </span>
                        </td>
                        <td>{f.baggage || '—'}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="overflow-x-auto rounded-2xl bg-white ring-1 ring-nkc-blue/10">
              <table className="v4-board-table v4-board-table--light min-w-full">
                <thead>
                  <tr>
                    <th>Airline</th>
                    <th>Flight</th>
                    <th>Destination</th>
                    <th>Scheduled</th>
                    <th>Actual</th>
                    <th>Status</th>
                    <th>Gate</th>
                    <th>Terminal</th>
                  </tr>
                </thead>
                <tbody>
                  {departures.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="!text-center text-nkc-muted">
                        {emptyMessage('dep')}
                      </td>
                    </tr>
                  ) : (
                    departures.map((f) => (
                      <tr key={`${f.flight}-${f.std}-${f.destinationCode}`} className="v4-board-row">
                        <td>
                          <AirlineCell flight={f} />
                        </td>
                        <td>
                          <strong className="text-nkc-navy">{f.flight}</strong>
                        </td>
                        <td>
                          <PlaceCell flight={f} dir="dep" lang={lang} />
                        </td>
                        <td className="font-mono tabular-nums text-nkc-muted">{f.std || '—'}</td>
                        <td className="font-mono font-semibold tabular-nums text-nkc-blue">
                          {(f.etd || '—') !== '—' ? f.etd : f.std || '—'}
                        </td>
                        <td>
                          <span className={`${statusClass(f.statusKey)} notranslate`} translate="no">
                            {localizedStatus(f, lang)}
                          </span>
                        </td>
                        <td>{f.gate || '—'}</td>
                        <td>{f.terminal || f.checkin || '—'}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
          <p className="mt-3 text-right text-xs text-nkc-muted">Last updated: {updatedLabel}</p>
        </div>
      </div>
    </section>
  );
}
